import React, { useState } from 'react';
import { Dialog } from '@/ui/components/Dialog';
import { Button } from '@/ui/components/Button';
import { TextField } from '@/ui/components/TextField';
import { useNotificationStore } from '@/store/notificationStore';
import { WalletSigningModal } from '@/components/transactions/WalletSigningModal';
import { toSigningPayload } from '@/lib/wallet';
import type { SigningPayload } from '@/types/signing';
import { placeAuctionBid, confirmAuctionBid, type PlaceBidResponse } from '@/api/auctions';

const DROPS_PER_XRP = 1_000_000;

interface Props {
  open: boolean;
  onClose: () => void;
  auctionId: string;
  minBidDrops: string;
  reservePriceDrops: string;
  highestBidDrops?: string;
  onBidConfirmed?: () => void;
}

function formatXrp(drops: string | undefined | null): string {
  if (!drops) return '—';
  return (Number(drops) / DROPS_PER_XRP).toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export const PlaceBidModal: React.FC<Props> = ({
  open,
  onClose,
  auctionId,
  minBidDrops,
  reservePriceDrops,
  highestBidDrops,
  onBidConfirmed,
}) => {
  const [bid, setBid] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState<SigningPayload | null>(null);

  const { success: showSuccess, error: showError } = useNotificationStore();

  function reset() {
    setBid('');
    setError(null);
    setSubmitting(false);
    setSigning(null);
  }

  async function handleSubmit() {
    setError(null);
    const parsed = Number(bid);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a valid bid amount');
      return;
    }
    const bidDrops = BigInt(Math.round(parsed * DROPS_PER_XRP));
    if (bidDrops < BigInt(minBidDrops)) {
      setError(`Bid must be at least ${formatXrp(minBidDrops)} XRP`);
      return;
    }
    if (highestBidDrops && bidDrops <= BigInt(highestBidDrops)) {
      setError(`Bid must exceed current highest (${formatXrp(highestBidDrops)} XRP)`);
      return;
    }

    setSubmitting(true);
    try {
      const res = (await placeAuctionBid(auctionId, {
        bidAmountDrops: bidDrops.toString(),
      })) as PlaceBidResponse;

      if (!res.success || !res.data) {
        setError(res.error || 'Failed to place bid');
        return;
      }

      setSigning(toSigningPayload(res.data.signingIntent ?? res.data.payload));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place bid');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSigned(txHash: string) {
    const res = await confirmAuctionBid(auctionId, txHash);
    if (res.success) {
      showSuccess('Bid placed!');
      onBidConfirmed?.();
      reset();
      onClose();
    } else {
      showError((res as any).error || 'Bid signed but on-chain confirmation failed');
    }
    setSigning(null);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) { reset(); onClose(); } }}>
        <Dialog.Content className="flex w-96 flex-col gap-4 p-6">
          <span className="text-heading-3 font-heading-3 text-default-font">Place a bid</span>

          <TextField
            label="Bid amount (XRP)"
            helpText="Keep enough XRP in your wallet to cover the bid until the auction ends. You can cancel anytime."
          >
            <TextField.Input
              type="number"
              inputMode="decimal"
              placeholder={`e.g., ${formatXrp(minBidDrops)}`}
              value={bid}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBid(e.target.value)}
            />
          </TextField>

          <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-2">
            <span className="text-caption font-caption text-subtext-color">
              Min bid {formatXrp(minBidDrops)} XRP — Reserve {formatXrp(reservePriceDrops)} XRP
            </span>
          </div>

          {highestBidDrops && (
            <span className="text-caption font-caption text-subtext-color">
              Current highest bid: {formatXrp(highestBidDrops)} XRP
            </span>
          )}

          {error && <span className="text-caption font-caption text-error-700">{error}</span>}

          <div className="flex w-full items-center gap-2">
            <Button
              className="h-10 w-auto flex-none grow"
              variant="neutral-secondary"
              size="large"
              onClick={() => { reset(); onClose(); }}
            >
              Cancel
            </Button>
            <Button
              className="h-10 w-auto flex-none grow"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Make offer'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog>

      {signing && (
        <WalletSigningModal
          open={true}
          onClose={() => setSigning(null)}
          title="Place auction bid"
          payload={signing}
          onSuccess={handleSigned}
          onRejected={() => {
            showError('Bid rejected');
            setSigning(null);
          }}
          onExpired={() => {
            showError('Sign request expired');
            setSigning(null);
          }}
        />
      )}
    </>
  );
};
